/** @ tagging helpers for Daily Notebook (aligned with issue comment @email-prefix style). */

export function normalizeMember(m) {
  const fullName = String(m.full_name || "").trim();
  const email = String(m.email || "").trim();
  const emailLocal = email.split("@")[0] || "";
  return {
    id: m.id,
    fullName,
    email,
    emailLocal,
    searchHaystack: [fullName, email, emailLocal, fullName.replace(/\s+/g, "")]
      .filter(Boolean)
      .map((s) => s.toLowerCase()),
    mentionInsert: fullName || emailLocal || `user${m.id}`,
  };
}

export function getMentionState(text, cursorPos) {
  const before = text.slice(0, cursorPos);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  const charBefore = at > 0 ? before[at - 1] : " ";
  if (charBefore && !/\s|[([{,;]/.test(charBefore)) return null;
  const query = before.slice(at + 1);
  if (query.includes("\n")) return null;
  return { start: at, query };
}

export function filterMembersForMention(members, query) {
  const q = String(query || "").trim().toLowerCase();
  const list = members.map(normalizeMember);
  if (!q) return list.slice(0, 8);
  return list
    .filter((m) => m.searchHaystack.some((h) => h.includes(q)))
    .slice(0, 8);
}

export function insertMention(text, mentionStart, cursorPos, insertLabel) {
  const before = text.slice(0, mentionStart);
  const after = text.slice(cursorPos);
  const token = `@${insertLabel} `;
  const next = `${before}${token}${after}`;
  return { text: next, cursor: before.length + token.length };
}

/**
 * Resolve assignee IDs from @tokens in body text.
 * Matches full name (longest first) or email local-part after @.
 */
export function resolveAssigneeIdsFromText(text, membersRaw) {
  const members = (membersRaw || []).map(normalizeMember);
  const ids = new Set();
  let i = 0;
  const sorted = [...members].sort(
    (a, b) => b.mentionInsert.length - a.mentionInsert.length
  );

  while (i < text.length) {
    if (text[i] !== "@") {
      i += 1;
      continue;
    }
    const rest = text.slice(i + 1);
    let matched = null;
    let matchedLen = 0;
    for (const m of sorted) {
      const candidates = [m.mentionInsert, m.emailLocal].filter(Boolean);
      for (const cand of candidates) {
        if (rest.toLowerCase().startsWith(cand.toLowerCase())) {
          const nextChar = rest[cand.length];
          if (nextChar === undefined || /[\s,.;!?)\]]/.test(nextChar)) {
            matched = m;
            matchedLen = cand.length;
            break;
          }
        }
      }
      if (matched) break;
    }
    if (matched) {
      ids.add(matched.id);
      i += 1 + matchedLen;
    } else {
      i += 1;
    }
  }
  return [...ids];
}

export function splitTextWithMentions(text) {
  const parts = [];
  const regex = /(@[^\s@]+(?:\s+[^\s@]+)*)/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: "text", value: text.slice(last, match.index) });
    }
    parts.push({ type: "mention", value: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: "text", value: text.slice(last) });
  }
  return parts.length ? parts : [{ type: "text", value: text || "" }];
}

/** "* ", "- ", "• ", "1. " or "1) " at the start of a pasted bullet. */
const BULLET_MARKER_RE = /^(?:[*\-•–●▪]|\d+[.)])\s+/;

/**
 * Split composer text into a heading and sub-tasks.
 *
 * The first non-blank line is the heading; every line after it is a sub-task.
 * Leading bullet markers are dropped, and indentation sets the depth (0-3) so
 * a nested bullet stays visibly nested. Depth is relative, not a fixed number
 * of spaces, because pasted lists mix tabs and 2/4-space indents.
 */
export function parseNotebookText(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");
  if (lines.length === 0) return { heading: "", items: [] };

  const [first, ...rest] = lines;
  const openIndents = [];
  const items = [];
  for (const raw of rest) {
    const indent = raw.match(/^[ \t]*/)[0].replace(/\t/g, "    ").length;
    const body = raw.trim().replace(BULLET_MARKER_RE, "").trim();
    if (!body) continue;
    while (openIndents.length && indent < openIndents[openIndents.length - 1]) openIndents.pop();
    if (!openIndents.length || indent > openIndents[openIndents.length - 1]) openIndents.push(indent);
    items.push({ text: body, depth: Math.min(3, openIndents.length - 1) });
  }
  return { heading: first.trim(), items };
}

/**
 * Rows created before the date prefix was dropped still start with
 * "[Daily Notebook · Oct 13] "; show them without it.
 */
export function stripNotebookPrefix(text) {
  return String(text || "").replace(/^\[Daily Notebook[^\]]*\]\s*/, "");
}

/**
 * Decide what a piece of composer text becomes.
 *
 *  heading  the first line names someone: it is the heading, the lines after
 *           it are sub-tasks, and a sub-task that names someone goes to them
 *           alone.
 *  single   the first line names nobody: there is no heading to hang
 *           sub-tasks on, so the whole text is ONE task with its line breaks
 *           kept, owned by whoever is named anywhere in it (one person or
 *           several).
 *  none     nobody is named at all - the caller asks for an owner.
 */
export function planNotebookEntry(text, members) {
  const full = String(text || "").replace(/\r\n?/g, "\n").trim();
  const { heading, items } = parseNotebookText(text);

  const headingOwners = resolveAssigneeIdsFromText(heading, members);
  if (headingOwners.length > 0) {
    return {
      kind: "heading",
      bodyText: heading,
      assigneeIds: headingOwners,
      items: items.map((item) => ({
        text: item.text,
        depth: item.depth,
        assignee_ids: resolveAssigneeIdsFromText(item.text, members),
      })),
    };
  }

  const wholeOwners = resolveAssigneeIdsFromText(full, members);
  return {
    kind: wholeOwners.length > 0 ? "single" : "none",
    bodyText: full,
    assigneeIds: wholeOwners,
    items: [],
  };
}
